import { useTranslation } from 'react-i18next';
import { cn } from '@shared/utils/utils';
import { WebhookEndpoint } from '../../models/Webhooks';
import { EndpointActionsMenu } from '../interaction/EndpointActionsMenu';

interface EndpointCardProps {
  endpoint: WebhookEndpoint;
  onEdit: (endpoint: WebhookEndpoint) => void;
  onTest: (id: number) => void;
  onToggleStatus: (
    id: number,
    url: WebhookEndpoint['url'],
    event_types: WebhookEndpoint['event_types'],
    currentStatus: WebhookEndpoint['status'],
  ) => void;
  onDelete: (id: number) => void;
}

const STATUS_DOT: Record<WebhookEndpoint['status'], string> = {
  active: 'bg-success',
  paused: 'bg-warning',
  disabled: 'bg-muted-foreground',
};

export function EndpointCard({ endpoint, onEdit, onTest, onToggleStatus, onDelete }: EndpointCardProps) {
  const { t } = useTranslation('webhooksModule');
  const name = endpoint.description?.trim();

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3.5 transition-colors"
      data-testid={`webhook-endpoint-${endpoint.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{name || t('endpoints.noName')}</p>
        <p className="mt-1 truncate text-sm text-muted-foreground">{endpoint.url}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground',
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[endpoint.status])} />
          {t(`endpoints.status.${endpoint.status}`)}
        </span>

        <EndpointActionsMenu
          endpoint={endpoint}
          onEdit={onEdit}
          onTest={onTest}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}
